/** @jsxImportSource @emotion/react */

import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";

import useInvoices from "../../hooks/use-invoices.js";
import { DataGrid } from "../../components/index.js";
import Filters, { getPresetDates } from "../../components/filters/filters.js";
import HeroMetrics from "../../components/hero-metrics/hero-metrics.js";
import InvoiceModal from "./InvoiceModal.js";

import { currencyFormatted } from "../../util.js";
import { LoginContext } from "../../context/login-context.js";
import usePersistedState from "../../hooks/use-persisted-state.js";
import ColumnsPanel from "../../components/datagrid/columns-panel.js";
import {
  CUSTOMER_TYPE_CHIP_COLORS,
  CUSTOMER_TYPE_FILTER_OPTIONS,
  CUSTOMER_TYPE_LABELS,
  CUSTOMER_TYPE_NONE_FILTER_VALUE,
  resolveCustomerType,
} from "../../components/constants.js";

dayjs.extend(utc);

const INVOICE_STATUS_OPTIONS = [
  { value: "NEW", label: "New" },
  { value: "REVIEW", label: "In Review" },
  { value: "APPROVED", label: "Approved" },
  { value: "PAID", label: "Paid" },
  { value: "REJECTED", label: "Not Approved" },
  { value: "NO_PAYMENT", label: "No Payment" },
];

const statusLabel = (status) => {
  const match = INVOICE_STATUS_OPTIONS.find((s) => s.value === status);
  return match ? match.label : status;
};

function BulkActionToolbar({
  selectedCount,
  onBulkStatusChange,
  bulkStatusSaving,
}) {
  const [targetStatus, setTargetStatus] = useState("");

  const handleApply = () => {
    if (targetStatus) {
      onBulkStatusChange(targetStatus);
      setTargetStatus("");
    }
  };

  if (selectedCount === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1,
        backgroundColor: "#EFF8FF",
        borderBottom: "1px solid #B2DDFF",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {selectedCount} invoice{selectedCount > 1 ? "s" : ""} selected
      </Typography>

      <Select
        size="small"
        displayEmpty
        value={targetStatus}
        onChange={(e) => setTargetStatus(e.target.value)}
        sx={{ minWidth: 160, backgroundColor: "#fff" }}
        renderValue={(v) => (v ? statusLabel(v) : "Set status…")}
      >
        {INVOICE_STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>

      <Button
        variant="contained"
        size="small"
        disabled={!targetStatus || bulkStatusSaving}
        onClick={handleApply}
        sx={{ textTransform: "none" }}
      >
        {bulkStatusSaving ? "Applying…" : "Apply"}
      </Button>
    </Box>
  );
}

function GridToolbar() {
  return (
    <GridToolbarContainer sx={{ height: "45px" }}>
      <GridToolbarFilterButton />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton
        slotProps={{ button: { title: "Manage Columns" } }}
      />
      <GridToolbarExport
        slotProps={{
          tooltip: { title: "Export data" },
          button: { variant: "outlined" },
        }}
      />
    </GridToolbarContainer>
  );
}

const COLUMN_DEFAULTS = {
  id: false,
  dealer: false,
  created_at: false,
  rebate_amount: true,
  customer_type: true,
  status_updated: false,
  review_status: false,
};

const GRID_DEFAULT_PRESET = 'ytd';

const InvoicesGrid = (props) => {
  const { farms, strategicAccounts, flattenStratAccounts, filterAddon } = props;
  const { state: userData } = useContext(LoginContext);

  const defaultGridDates = getPresetDates(GRID_DEFAULT_PRESET);
  const [gridPreset, setGridPreset] = usePersistedState('gridPreset', GRID_DEFAULT_PRESET);
  const [gridStartISO, setGridStartISO] = usePersistedState('gridStartDate', defaultGridDates.startDate.toISOString());
  const [gridEndISO, setGridEndISO] = usePersistedState('gridEndDate', defaultGridDates.endDate.toISOString());

  useEffect(() => {
    if (gridPreset && gridPreset !== 'custom') {
      const { startDate: fresh, endDate: freshEnd } = getPresetDates(gridPreset);
      if (fresh && freshEnd) {
        setGridStartISO(fresh.toISOString());
        setGridEndISO(freshEnd.toISOString());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDate = gridStartISO ? new Date(gridStartISO) : null;
  const endDate = gridEndISO ? new Date(gridEndISO) : null;

  const setStartDate = (date) => setGridStartISO(date ? date.toISOString() : null);
  const setEndDate = (date) => setGridEndISO(date ? date.toISOString() : null);

  const [selectedFarms, setSelectedFarms] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [snackbar, setSnackbar] = useState(null);

  const [sortModel, setSortModel] = useState([
    { field: "created_at", sort: "desc" },
  ]);
  const [columnVisibility, setColumnVisibility] = usePersistedState(
    'invoicesCols',
    COLUMN_DEFAULTS
  );

  const {
    invoices,
    selectedInvoice,
    showInvoice,
    setShowInvoice,
    setSelectedInvoice,
    refetchInvoices,
    handleSaveInvoiceItem,
    handleDeleteInvoiceItem,
    handleBulkDeleteInvoiceItems,
    handleUpdateInvoiceStatus,
    handleBulkUpdateInvoiceStatus,
    handleDeleteInvoice,
    handleUpdateInvoiceDetails,
    updateInvoiceDetailsComplete,
    updatingInvoiceDetails,
    error,
    isSuccess,
    isLoadingInvoices,
    isRefetchingInvoices,
    isDeleting,
    bulkItemDeleting,
    isSaving,
    bulkStatusSaving,
    bulkStatusSaveSuccess,
    bulkInvoiceStatusUpdateError,
  } = useInvoices({ selectedFarms, startDate, endDate });

  useEffect(() => {
    if (gridStartISO && gridEndISO) {
      refetchInvoices();
    }
  }, [gridStartISO, gridEndISO, selectedFarms, refetchInvoices]);

  useEffect(() => {
    if (bulkStatusSaveSuccess) {
      setSnackbar({ children: "Status updated successfully", severity: "success" });
      setRowSelectionModel([]);
    }
  }, [bulkStatusSaveSuccess]);

  useEffect(() => {
    if (bulkInvoiceStatusUpdateError) {
      setSnackbar({ children: "Error updating status", severity: "error" });
    }
  }, [bulkInvoiceStatusUpdateError]);

  const handleBulkStatusChange = (status) => {
    handleBulkUpdateInvoiceStatus({
      invoice_ids: rowSelectionModel,
      status,
    });
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },

    {
      field: "status",
      headerName: "Status",
      width: 130,
      type: "singleSelect",
      valueOptions: INVOICE_STATUS_OPTIONS.map((s) => ({
        value: s.value,
        label: s.label,
      })),
      valueFormatter: (value) => statusLabel(value),
    },

    {
      field: "strategic_account",
      headerName: "Strategic Account",
      width: 200,
      valueGetter: (_, row) => {
        const accounts = [
          row?.account?.parent_account?.parent_account?.parent_account,
          row?.account?.parent_account?.parent_account,
          row?.account?.parent_account,
          row?.account,
        ].filter(Boolean);
        return (
          accounts.find((a) => a.account_type === "STRATEGIC_ACCOUNT")?.name ||
          ""
        );
      },
    },

    {
      field: "customer_type",
      headerName: "Customer Type",
      width: 150,
      type: "singleSelect",
      valueOptions: CUSTOMER_TYPE_FILTER_OPTIONS,
      valueGetter: (_, row) =>
        resolveCustomerType(row?.account) || CUSTOMER_TYPE_NONE_FILTER_VALUE,
      valueFormatter: (value) =>
        !value || value === CUSTOMER_TYPE_NONE_FILTER_VALUE
          ? "None"
          : CUSTOMER_TYPE_LABELS[value] || value,
      renderCell: (params) => {
        const customerType = params.value;
        if (!customerType || customerType === CUSTOMER_TYPE_NONE_FILTER_VALUE) {
          return "None";
        }
        const chipColors = CUSTOMER_TYPE_CHIP_COLORS[customerType];
        if (!chipColors) {
          return CUSTOMER_TYPE_LABELS[customerType] || customerType;
        }
        return (
          <Chip
            label={CUSTOMER_TYPE_LABELS[customerType]}
            size="small"
            sx={{
              backgroundColor: chipColors.bg,
              color: chipColors.color,
              fontWeight: 500,
            }}
          />
        );
      },
    },

    {
      field: "farm",
      headerName: "Farm",
      width: 175,
      valueGetter: (_, row) => {
        const accounts = [
          row?.account?.parent_account?.parent_account?.parent_account,
          row?.account?.parent_account?.parent_account,
          row?.account?.parent_account,
          row?.account,
        ].filter(Boolean);
        return accounts.find((a) => a.account_type === "FARM")?.name || "";
      },
    },

    {
      field: "dealer",
      headerName: "Dealer",
      width: 180,
    },

    {
      field: "invoice_number",
      headerName: "Invoice #",
      width: 130,
    },

    {
      field: "invoice_date",
      headerName: "Invoice Date",
      width: 150,
      type: "date",
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) =>
        value ? dayjs.utc(value).format("MMM DD, YYYY") : "",
    },

    {
      field: "created_at",
      headerName: "Upload Date",
      width: 150,
      type: "date",
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) =>
        value ? dayjs.utc(value).format("MMM DD, YYYY") : "",
    },

    {
      field: "total_amount",
      headerName: "Invoice Total",
      width: 140,
      type: "number",
      valueGetter: (_, row) => {
        return (
          row?.invoice_items?.reduce(
            (acc, item) => acc + (item.quantity || 0) * (item.retail_price || 0),
            0
          ) || 0
        );
      },
      valueFormatter: (value) => currencyFormatted(value),
    },

    {
      field: "rebate_amount",
      headerName: "Rebate Amount",
      width: 140,
      type: "number",
      valueGetter: (_, row) => {
        const rebateAmt = row?.rebate_amt || 0;
        return (
          row?.invoice_items?.reduce(
            (acc, item) =>
              acc +
              (item?.eligibility === "ELIGIBLE"
                ? (item?.quantity || 0) *
                  ((item?.retail_price || 0) * rebateAmt)
                : 0),
            0
          ) || 0
        );
      },
      valueFormatter: (value) => currencyFormatted(value),
    },

    {
      field: "status_updated",
      headerName: "Status Updated",
      width: 160,
      type: "date",
      valueGetter: (value) => value && new Date(value),
      valueFormatter: (value) =>
        value ? dayjs.utc(value).format("MMM DD, YYYY") : "",
    },

    ...(userData?.isValmontUser ? [
      {
        field: "is_aftermarket_part_review",
        headerName: "Aftermarket Review",
        width: 160,
        filterable: false,
        valueFormatter: (value) => value ? "Reviewed" : "Needs Review",
      },
      {
        field: "is_machine_part_review",
        headerName: "Machine Review",
        width: 160,
        filterable: false,
        valueFormatter: (value) => value ? "Reviewed" : "Needs Review",
      },
      {
        field: "review_status",
        headerName: "Review Status",
        width: 180,
        type: "singleSelect",
        hideable: false,
        valueOptions: [
          { value: "AFTERMARKET_COMPLETE", label: "Aftermarket complete" },
          { value: "AFTERMARKET_NOT_COMPLETE", label: "Aftermarket not complete" },
          { value: "MACHINE_COMPLETE", label: "Machine complete" },
          { value: "MACHINE_NOT_COMPLETE", label: "Machine not complete" },
          { value: "BOTH_COMPLETE", label: "Both complete" },
          { value: "BOTH_NOT_COMPLETE", label: "Both not complete" },
        ],
        valueGetter: (_, row) => {
          if (row.is_aftermarket_part_review && row.is_machine_part_review) return "BOTH_COMPLETE";
          if (row.is_aftermarket_part_review) return "AFTERMARKET_COMPLETE";
          if (row.is_machine_part_review) return "MACHINE_COMPLETE";
          return "BOTH_NOT_COMPLETE";
        },
        valueFormatter: (value) => {
          const labels = {
            BOTH_COMPLETE: "Both complete",
            AFTERMARKET_COMPLETE: "Aftermarket complete",
            MACHINE_COMPLETE: "Machine complete",
            BOTH_NOT_COMPLETE: "Neither complete",
          };
          return labels[value] || "Needs Review";
        },
        filterOperators: getGridSingleSelectOperators().filter(op => op.value === 'is').map(op => ({
          ...op,
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            return (_, row) => {
              const am = row.is_aftermarket_part_review === true;
              const mc = row.is_machine_part_review === true;
              switch (filterItem.value) {
                case "AFTERMARKET_COMPLETE": return am;
                case "AFTERMARKET_NOT_COMPLETE": return !am;
                case "MACHINE_COMPLETE": return mc;
                case "MACHINE_NOT_COMPLETE": return !mc;
                case "BOTH_COMPLETE": return am && mc;
                case "BOTH_NOT_COMPLETE": return !am && !mc;
                default: return true;
              }
            };
          },
        })),
      },
    ] : []),
  ];

  const rows = invoices || [];

  return (
    <>
      <Filters
        farms={farms}
        strategicAccounts={strategicAccounts}
        startDate={startDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        endDate={endDate}
        selectedFarms={selectedFarms}
        setSelectedFarms={setSelectedFarms}
        multiFarmSelect={true}
        preset={gridPreset}
        onPresetChange={setGridPreset}
      >
        {filterAddon}
      </Filters>

      <HeroMetrics invoiceLoading={isLoadingInvoices && !invoices} invoices={invoices} />

      <BulkActionToolbar
        selectedCount={rowSelectionModel.length}
        onBulkStatusChange={handleBulkStatusChange}
        bulkStatusSaving={bulkStatusSaving}
      />

      <DataGrid
        slots={{ toolbar: GridToolbar, columnsPanel: ColumnsPanel }}
        slotProps={{
          columnsPanel: { defaults: COLUMN_DEFAULTS },
          columnsManagement: {
            getTogglableColumns: (columns) =>
              columns
                .filter((col) => col.field !== "review_status")
                .map((col) => col.field),
          },
        }}
        loading={isLoadingInvoices || isRefetchingInvoices}
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newModel) =>
          setRowSelectionModel(newModel)
        }
        onRowClick={(params) => {
          setSelectedInvoice(params.row);
          setShowInvoice(true);
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={(newVisibility) =>
          setColumnVisibility(newVisibility)
        }
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
        }}
      />

      <InvoiceModal
        strategicAccounts={strategicAccounts}
        flattenStratAccounts={flattenStratAccounts}
        open={showInvoice}
        setOpen={setShowInvoice}
        invoice={selectedInvoice}
        saveItem={handleSaveInvoiceItem}
        deleteItem={handleDeleteInvoiceItem}
        bulkDeleteItems={handleBulkDeleteInvoiceItems}
        isBulkDeleting={bulkItemDeleting}
        deleteInvoice={handleDeleteInvoice}
        error={error}
        isSuccess={isSuccess}
        isLoading={isLoadingInvoices || isRefetchingInvoices || isDeleting || isSaving}
        refetch={refetchInvoices}
        handleUpdateInvoiceDetails={handleUpdateInvoiceDetails}
        updateInvoiceDetails={handleUpdateInvoiceDetails}
        updateInvoiceDetailsComplete={updateInvoiceDetailsComplete}
        updatingInvoiceDetails={updatingInvoiceDetails}
      />

      {snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.children}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default InvoicesGrid;
